/**
 * bundle-watcher.js
 *
 * THREE swap paths:
 *   A. Fetch interceptor - component add completes a bundle -> block add, swap to bundle
 *   B. Fetch interceptor - bundle added directly with components in cart -> pre-clear components
 *   C. Safety net (evaluateCart) - components in cart with no bundle, upgrade, or orphan cleanup
 *
 * window.__isSwapping prevents re-entrant loops.
 * _originalFetch bypasses this interceptor for all internal API calls.
 */

(function () {
  'use strict';

  // MUST be captured before wrapping window.fetch
  const _originalFetch = window.fetch.bind(window);

  // ─── Resilience helpers (rate-limit protection) ───────────────────────

  // Short-lived cart cache - dedupes /cart.js calls within rapid evaluations.
  // TTL intentionally short (300ms) so we don't risk decisions on stale data.
  const cartCache = {
    data: null,
    timestamp: 0,
    TTL_MS: 300,
    get() {
      if (!this.data) return null;
      if (Date.now() - this.timestamp > this.TTL_MS) return null;
      return this.data;
    },
    set(cart) {
      this.data = cart;
      this.timestamp = Date.now();
    },
    invalidate() {
      this.data = null;
      this.timestamp = 0;
    },
  };

  // Retry wrapper for critical cart mutations.
  // Handles 429 with exponential backoff, respects Retry-After header.
  // On final failure, returns the 429 response so caller handles gracefully.
  async function fetchWithRetry(url, options, maxRetries = 2) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      let res;
      try {
        res = await _originalFetch(url, options);
      } catch (networkErr) {
        if (attempt === maxRetries) throw networkErr;
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        continue;
      }

      if (res.status !== 429) return res;

      if (attempt === maxRetries) {
        console.warn(`[BundleWatcher] 429 on ${url} after ${maxRetries} retries - giving up`);
        return res;
      }

      const retryAfter = res.headers.get('Retry-After');
      const waitMs = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : 500 * (attempt + 1); // 500ms, 1000ms

      console.warn(`[BundleWatcher] 429 on ${url} - retry ${attempt + 1}/${maxRetries} after ${waitMs}ms`);
      await new Promise((r) => setTimeout(r, waitMs));
    }
  }

  const DEBOUNCE_MS = 400;

  window.__isSwapping = window.__isSwapping || false;
  let debounceTimer = null;

  // Helpers

  // How old window.__sharedCartState may be before the interceptor re-fetches
  // /cart.js. Every cart mutation in this theme ends in a drawer refresh that
  // rewrites the shared state (and performSwap below writes it too), so within
  // a page it only goes stale for the ~0.5s a refresh is in flight. 3s also
  // covers the hover→click gap the prefetch below relies on.
  const SHARED_CART_MAX_AGE_MS = 3000;

  function setSharedCartState(cart) {
    cartCache.set(cart);
    window.__sharedCartState = { data: cart, ts: Date.now() };
  }

  function invalidateSharedCartState() {
    cartCache.invalidate();
    if (window.__sharedCartState) window.__sharedCartState.ts = 0;
  }

  // The interceptor sits between the shopper's click and their /cart/add.js.
  // Shopify's /cart.js normally answers in 100–300ms, but it has been seen
  // taking 13s; past this budget the add goes through untouched and the
  // safety net (evaluateCart) merges the bundle afterwards.
  const INTERCEPT_CART_FETCH_TIMEOUT_MS = 1500;

  async function fetchCart(options = {}) {
    // Prefer the shared state written by cart-drawer-custom / cart-upsell.
    const shared = window.__sharedCartState;
    if (shared?.data && Date.now() - shared.ts < SHARED_CART_MAX_AGE_MS) return shared.data;

    // Fall back to own short-lived cache.
    const cached = cartCache.get();
    if (cached) return cached;

    const timeoutMs = Number(options.timeoutMs || 0);
    const controller = timeoutMs > 0 && typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    try {
      const res = await _originalFetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined,
      });
      if (!res.ok) return null;

      const cart = await res.json();
      setSharedCartState(cart);
      return cart;
    } catch (e) {
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // Add-to-cart buttons: warm the cart state while the pointer/finger is on the
  // button, so the interceptor's decision on click (does this add complete a
  // bundle?) needs no /cart.js round-trip — that fetch alone was 140–300ms of
  // spinner on every component add.
  const ADD_TO_CART_HOVER_SELECTOR = [
    '.js-ajax-add-to-cart',
    '[data-cart-upsell-button]',
    'form[action*="/cart/add"] button',
    'form[action*="/cart/add"] [type="submit"]',
  ].join(', ');
  let prefetchInFlight = false;

  function prefetchCartForAddToCart(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest(ADD_TO_CART_HOVER_SELECTOR)) return;
    if (prefetchInFlight || window.__isSwapping) return;
    const shared = window.__sharedCartState;
    if (shared?.data && Date.now() - shared.ts < SHARED_CART_MAX_AGE_MS) return;
    prefetchInFlight = true;
    fetchCart().finally(() => { prefetchInFlight = false; });
  }

  document.addEventListener('pointerover', prefetchCartForAddToCart, { capture: true, passive: true });
  document.addEventListener('touchstart', prefetchCartForAddToCart, { capture: true, passive: true });
  document.addEventListener('focusin', prefetchCartForAddToCart, true);

  function getSortedConfigs() {
    const configs = window.BUNDLE_CONFIGS;
    if (!Array.isArray(configs) || configs.length === 0) return [];
    return [...configs].sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  function getRelevantVariantIdsForConfigs(configs) {
    const relevant = new Set();

    for (const config of configs) {
      const bundleVariantId = Number(config?.bundle_product?.variantId || 0);
      if (bundleVariantId > 0) relevant.add(bundleVariantId);

      const componentProducts = Array.isArray(config?.component_products) ? config.component_products : [];
      for (const component of componentProducts) {
        const componentVariantId = Number(component?.variantId || 0);
        if (componentVariantId > 0) relevant.add(componentVariantId);
      }
    }

    return relevant;
  }

  function showBundleSavingsNotification(config) {
    // Pull the message from the same "Savings flash message" setting used by
    // the cart drawer upsell (rendered into data-savings-template by
    // snippets/cart-upsell.liquid). Falls back to the dynamic default when the
    // setting is blank or the element isn't present.
    const savingsTextEl = document.querySelector('[data-cart-upsell-savings-text][data-savings-template]');
    const customMessage = savingsTextEl ? savingsTextEl.dataset.savingsTemplate : '';

    const savingsPercentage = Number(config?.savings_percentage || 0);
    const fallbackMessage = `You just saved ${Math.max(0, savingsPercentage)}% by bundling.`;
    const message = customMessage || fallbackMessage;

    const existingToast = document.getElementById('bundle-watcher-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.id = 'bundle-watcher-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    if (customMessage) {
      toast.innerHTML = customMessage;
      // Richtext wraps content in <p>; drop its default margin inside the pill.
      toast.querySelectorAll('p').forEach((p) => {
        p.style.margin = '0';
      });
    } else {
      toast.textContent = message;
    }
    toast.style.position = 'fixed';
    toast.style.left = '50%';
    toast.style.bottom = '24px';
    toast.style.transform = 'translateX(-50%)';
    toast.style.zIndex = '2147483647';
    toast.style.background = '#1f2937';
    toast.style.color = '#ffffff';
    toast.style.padding = '10px 14px';
    toast.style.borderRadius = '9999px';
    toast.style.fontSize = '14px';
    toast.style.lineHeight = '1.2';
    toast.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 180ms ease';

    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });

    window.setTimeout(() => {
      toast.style.opacity = '0';
      window.setTimeout(() => {
        if (toast.isConnected) toast.remove();
      }, 180);
    }, 3200);

    console.log('[BundleWatcher] Notification shown:', message);
  }

  // Matchers

  // A config's "Component Products" metaobject field can only reference each
  // product once. A multipack of a single item (eg. "2x Elemental Serum") is
  // therefore configured with just ONE component entry -- that single entry
  // means "2 units required", not 1. Configs with 2+ distinct components keep
  // the normal 1-of-each-product requirement.
  // Lines added by the Lip builder carry a `_bundle_id` property and are priced by
  // the bundle app's discount function: they never count towards a Lip Collection swap.
  function isBuilderLine(item) {
    return Boolean(item?.properties?._bundle_id);
  }

  function getRequiredComponentQty(config) {
    const componentProducts = Array.isArray(config?.component_products) ? config.component_products : [];
    return componentProducts.length === 1 ? 2 : 1;
  }

  // A bundle line carries a single purchase type. Merging a subscribed component
  // with a one-time one would force one of them onto the other's plan, and a
  // bundle product with no selling plans (eg. Elm Bare Essentials) can't carry a
  // subscription at all -- it would silently drop it. So components only merge
  // when they share one purchase type the bundle product can be sold as.
  function isSubscriptionLine(line) {
    return !!line?.selling_plan_allocation;
  }

  function bundleSupportsPurchaseType(config, subscribed) {
    return !subscribed || (config?.bundle_product?.sellingPlanIds || []).length > 0;
  }

  function findLineOfType(cartItems, variantId, subscribed, minQty = 0) {
    return cartItems.find((item) =>
      Number(item.variant_id) === variantId &&
      isSubscriptionLine(item) === subscribed &&
      Number(item.quantity || 0) >= minQty
    ) || null;
  }

  function findBundleForIncomingAdd(cart, newVariantIds, newVariantQuantities, newItemsHavePlan, configs) {
    const cartItems = (cart?.items || []).filter((item) => !isBuilderLine(item));

    for (const config of configs) {
      const componentIds = (config.component_products || []).map((p) => Number(p.variantId));
      if (componentIds.length === 0) continue;

      if (!componentIds.some((id) => newVariantIds.includes(id))) continue;

      if (!bundleSupportsPurchaseType(config, newItemsHavePlan)) continue;

      const requiredQty = getRequiredComponentQty(config);

      let allCovered = true;
      const existingLines = [];

      for (const id of componentIds) {
        const cartLine = findLineOfType(cartItems, id, newItemsHavePlan);
        const cartQty = cartLine ? Number(cartLine.quantity || 0) : 0;
        const incomingQty = newVariantQuantities.get(id) || 0;

        if (cartQty + incomingQty < requiredQty) {
          allCovered = false;
          break;
        }

        // Only the portion NOT covered by this incoming add needs to come out
        // of what's already sitting in the cart (the incoming add itself is
        // blocked by the interceptor and never lands in the cart).
        const neededFromCart = Math.max(0, requiredQty - incomingQty);
        if (neededFromCart > 0) {
          if (!cartLine || cartQty < neededFromCart) {
            allCovered = false;
            break;
          }
          existingLines.push({ line: cartLine, qty: neededFromCart });
        }
      }

      if (!allCovered) continue;

      return { config, existingLines, isSubscribed: newItemsHavePlan };
    }

    return null;
  }

  /**
   * Path A2: an incoming add is the "missing component" that combines with a
   * lower-tier bundle already in cart to form an upgrade bundle.
   *
   * Returns only lines already in cart (lower bundle line). Incoming item is
   * blocked by interceptor mock response and never enters cart.
   */
  function findUpgradeForIncomingAdd(cart, newVariantIds, newItemsHavePlan, configs) {
    const cartItems = (cart?.items || []).filter((item) => !isBuilderLine(item));

    for (const config of configs) {
      if (!config.is_upgrade_bundle) continue;

      const upgradeFromIds = (config.upgrade_from_bundles || []).map(Number);
      if (upgradeFromIds.length === 0) continue;

      if (!bundleSupportsPurchaseType(config, newItemsHavePlan)) continue;

      const lowerBundleLine = cartItems.find((item) =>
        upgradeFromIds.includes(Number(item.product_id)) && isSubscriptionLine(item) === newItemsHavePlan
      );
      if (!lowerBundleLine) continue;

      const lowerConfig = configs.find(
        (c) => Number(c?.bundle_product?.id || 0) === Number(lowerBundleLine.product_id)
      );
      if (!lowerConfig) continue;

      const lowerComponentProductIds = (lowerConfig.component_products || []).map((p) => Number(p.id));
      const upperComponentProductIds = (config.component_products || []).map((p) => Number(p.id));

      const missingProductId = upperComponentProductIds.find(
        (id) => !lowerComponentProductIds.includes(id)
      );
      if (!missingProductId) continue;

      const missingConfig = (config.component_products || []).find(
        (p) => Number(p.id) === missingProductId
      );
      if (!missingConfig) continue;
      const missingVariantId = Number(missingConfig.variantId || 0);

      if (!newVariantIds.includes(missingVariantId)) continue;

      return {
        config,
        linesToRemove: [lowerBundleLine],
        isSubscribed: newItemsHavePlan,
      };
    }

    return null;
  }

  function findMatchingBundle(cart, configs) {
    const cartItems = (cart?.items || []).filter((item) => !isBuilderLine(item));

    for (const config of configs) {
      const componentIds = (config.component_products || []).map((p) => Number(p.variantId));
      if (componentIds.length === 0) continue;

      const requiredQty = getRequiredComponentQty(config);

      for (const isSubscribed of [false, true]) {
        if (!bundleSupportsPurchaseType(config, isSubscribed)) continue;

        const componentLines = componentIds.map((id) => {
          const line = findLineOfType(cartItems, id, isSubscribed, requiredQty);
          return line ? { line, qty: requiredQty } : null;
        });
        if (componentLines.some((l) => !l)) continue;

        return { config, componentLines, isSubscribed };
      }
    }

    return null;
  }

  function findBundleUpgrade(cart, configs) {
    const cartItems = (cart?.items || []).filter((item) => !isBuilderLine(item));

    for (const config of configs) {
      if (!config.is_upgrade_bundle) continue;

      const upgradeFromIds = (config.upgrade_from_bundles || []).map(Number);
      if (upgradeFromIds.length === 0) continue;

      const lowerBundleLines = cartItems.filter((item) =>
        upgradeFromIds.includes(Number(item.product_id))
      );

      for (const lowerBundleLine of lowerBundleLines) {
        const isSubscribed = isSubscriptionLine(lowerBundleLine);
        if (!bundleSupportsPurchaseType(config, isSubscribed)) continue;

        // Find the lower-tier bundle config to know which components it already includes.
        const lowerBundleProductId = Number(lowerBundleLine.product_id || 0);
        const lowerBundleConfig = configs.find((candidate) => {
          const candidateBundleProductId = Number(candidate?.bundle_product?.id || 0);
          return candidateBundleProductId > 0 && candidateBundleProductId === lowerBundleProductId;
        });
        if (!lowerBundleConfig) continue;

        const upperComponentIds = (config.component_products || []).map((p) => Number(p.variantId)).filter(Boolean);
        const lowerComponentIds = (lowerBundleConfig.component_products || []).map((p) => Number(p.variantId)).filter(Boolean);
        if (upperComponentIds.length === 0 || lowerComponentIds.length === 0) continue;

        const lowerComponentSet = new Set(lowerComponentIds);
        const missingComponentIds = upperComponentIds.filter((id) => !lowerComponentSet.has(id));
        if (missingComponentIds.length === 0) continue;

        // Upgrade is valid only if all missing components exist as separate lines
        // in the cart, on the same purchase type as the lower-tier bundle.
        const missingLines = missingComponentIds
          .map((id) => findLineOfType(cartItems, id, isSubscribed))
          .filter(Boolean);
        if (missingLines.length !== missingComponentIds.length) continue;

        const linesToRemove = [lowerBundleLine, ...missingLines];

        return { config, linesToRemove, isSubscribed };
      }
    }

    return null;
  }

  function findOrphanedComponents(cart, configs) {
    const cartItems = (cart?.items || []).filter((item) => !isBuilderLine(item));

    for (const config of configs) {
      const bundleVariantId = Number(config.bundle_product?.variantId || 0);
      if (!bundleVariantId) continue;

      if (!cartItems.some((item) => Number(item.variant_id) === bundleVariantId)) continue;

      const componentIds = (config.component_products || []).map((p) => Number(p.variantId));
      const orphanedLines = componentIds
        .map((id) => cartItems.find((item) => Number(item.variant_id) === id))
        // Prevent repeated decrements on intentional extra component quantities.
        .filter((line) => Number(line?.quantity || 0) <= 1)
        .filter(Boolean);

      if (orphanedLines.length > 0) {
        return { orphanedLines };
      }
    }

    return null;
  }

  // Swap executor

  // Accepts either plain cart line objects (decremented by 1, the upgrade-bundle
  // paths' shape) or { line, qty } pairs (the qty to remove for that line, used
  // by the regular component-matching paths so multipacks decrement correctly).
  function buildDecrementUpdates(entries) {
    const updates = {};

    for (const entry of entries) {
      const line = entry?.line ? entry.line : entry;
      const qtyToRemove = entry?.line ? Number(entry.qty || 1) : 1;
      const lineKey = String(line?.key || '').trim();
      const fallbackVariantId = Number(line?.variant_id || 0);
      const currentQty = Number(line?.quantity || 0);
      const nextQty = Math.max(0, currentQty - qtyToRemove);

      if (lineKey) {
        updates[lineKey] = nextQty;
      } else if (fallbackVariantId > 0) {
        updates[String(fallbackVariantId)] = nextQty;
      }
    }

    return updates;
  }

  // One-time merges collapse into ONE /cart/update.js: Shopify's update endpoint
  // zeroes the component lines and adds the bundle variant in the same request
  // ("adds new line items to the cart if the variant_id provided doesn't match
  // any line item already in the cart"). The old update-then-add pair cost two
  // sequential cart mutations (0.5–1.1s each on this store) — most of the ATC
  // spinner whenever a bundle formed. Two cases still need the pair: a
  // subscription bundle (update.js can't set a selling_plan on a new line) and
  // a bundle variant already in the cart (update.js keys by variant id, so with
  // a subscription + a one-time line of that variant the target is ambiguous).
  //
  // `passthrough` carries the intercepted add's `sections` / `sections_url`
  // (bundled section rendering) into the final mutation, so the caller's cart
  // drawer gets its markup back exactly as if its own add had gone through.
  //
  // Resolves to false on failure, otherwise { sections } (possibly undefined).
  async function performSwap(config, linesToRemove, isSubscribed, passthrough = {}, cart = null) {
    const updates = buildDecrementUpdates(linesToRemove);
    const bundleVariantId = Number(config.bundle_product.variantId);
    const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

    const planIds = config.bundle_product?.sellingPlanIds || [];
    const sellingPlanId = isSubscribed && planIds.length > 0 ? planIds[0] : null;

    const sectionParams = {};
    if (passthrough && passthrough.sections) sectionParams.sections = passthrough.sections;
    if (passthrough && passthrough.sections_url) sectionParams.sections_url = passthrough.sections_url;

    const readJson = async (res) => { try { return await res.json(); } catch (_) { return null; } };
    const warn = async (label, res) => {
      let errBody = '';
      try { errBody = await res.text(); } catch (_) {}
      console.warn(`[BundleWatcher] ${label}:`, res.status, errBody);
    };

    const bundleAlreadyInCart = (cart?.items || []).some((item) => Number(item?.variant_id) === bundleVariantId);

    if (!sellingPlanId && !bundleAlreadyInCart) {
      updates[String(bundleVariantId)] = 1;

      const swapRes = await fetchWithRetry('/cart/update.js', {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ updates, ...sectionParams }),
      });
      if (!swapRes.ok) {
        await warn('Single-request swap failed', swapRes);
        return false;
      }

      const payload = await readJson(swapRes);
      if (payload && Array.isArray(payload.items)) {
        const { sections, ...nextCart } = payload;
        setSharedCartState(nextCart);
        console.log('[BundleWatcher] Bundle added →', config.bundle_product.title);
        return { sections };
      }

      invalidateSharedCartState();
      console.log('[BundleWatcher] Bundle added →', config.bundle_product.title);
      return { sections: undefined };
    }

    const removeRes = await fetchWithRetry('/cart/update.js', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ updates }),
    });
    if (!removeRes.ok) {
      await warn('Failed to remove components', removeRes);
      return false;
    }

    invalidateSharedCartState();

    const addItem = { id: bundleVariantId, quantity: 1 };
    if (sellingPlanId) addItem.selling_plan = sellingPlanId;

    const addRes = await fetchWithRetry('/cart/add.js', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ items: [addItem], ...sectionParams }),
    });

    if (!addRes.ok) {
      await warn('Failed to add bundle', addRes);
      return false;
    }

    invalidateSharedCartState();
    const addPayload = await readJson(addRes);
    console.log('[BundleWatcher] Bundle added →', config.bundle_product.title);
    return { sections: addPayload?.sections };
  }

  // The blocked add's caller still gets a 200 shaped like /cart/add.js — plus
  // the bundled `sections` it asked for, rendered after the swap.
  function mockAddResponse(config, swap) {
    const body = { items: [{ variant_id: config.bundle_product.variantId, quantity: 1 }] };
    if (swap && swap.sections) body.sections = swap.sections;
    return new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // Path A + B: Fetch interceptor

  window.fetch = async function bundleWatcherFetch(input, init) {
    const url = typeof input === 'string' ? input : (input?.url || '');

    if (url.includes('/cart/add.js') && !window.__isSwapping) {
      let addBody = null;
      try { addBody = JSON.parse(init?.body || 'null'); } catch (_) {}

      if (addBody) {
        const newItems = addBody.items
          ? addBody.items
          : (addBody.id ? [{ id: addBody.id, quantity: addBody.quantity, selling_plan: addBody.selling_plan }] : []);

        const newVariantIds = newItems.map((i) => Number(i.id)).filter(Boolean);
        const newItemsHavePlan = newItems.some((i) => !!i.selling_plan);

        if (newItems.length > 0 && newItems.every((i) => i?.properties?._bundle_id)) {
          return _originalFetch(input, init);
        }

        // Bundled section rendering requested by the caller — forwarded to the
        // swap's final mutation so its response carries the same sections.
        const passthrough = {};
        if (addBody.sections) passthrough.sections = addBody.sections;
        if (addBody.sections_url) passthrough.sections_url = addBody.sections_url;

        const newVariantQuantities = new Map();
        for (const item of newItems) {
          const id = Number(item.id);
          if (!id) continue;
          const qty = Number(item.quantity || 1);
          newVariantQuantities.set(id, (newVariantQuantities.get(id) || 0) + qty);
        }

        if (newVariantIds.length > 0) {
          const configs = getSortedConfigs();
          if (configs.length > 0) {
            const relevantVariantIds = getRelevantVariantIdsForConfigs(configs);
            const hasRelevantIncomingVariant = newVariantIds.some((id) => relevantVariantIds.has(id));

            if (!hasRelevantIncomingVariant) {
              return _originalFetch(input, init);
            }

            const cart = await fetchCart({ timeoutMs: INTERCEPT_CART_FETCH_TIMEOUT_MS });
            if (cart) {
              const upgradeMatch = findUpgradeForIncomingAdd(cart, newVariantIds, newItemsHavePlan, configs);

              if (upgradeMatch) {
                window.__isSwapping = true;
                try {
                  const swap = await performSwap(
                    upgradeMatch.config,
                    upgradeMatch.linesToRemove,
                    upgradeMatch.isSubscribed,
                    passthrough,
                    cart
                  );
                  if (swap) {
                    document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
                    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

                    console.log('[BundleWatcher] Path A2: upgraded to', upgradeMatch.config.bundle_product.title);

                    return mockAddResponse(upgradeMatch.config, swap);
                  }
                } catch (e) {
                  console.error('[BundleWatcher] Path A2 upgrade swap failed:', e);
                } finally {
                  window.__isSwapping = false;
                }
              }

              const match = findBundleForIncomingAdd(cart, newVariantIds, newVariantQuantities, newItemsHavePlan, configs);

              if (match) {
                window.__isSwapping = true;
                try {
                  const swap = await performSwap(match.config, match.existingLines, match.isSubscribed, passthrough, cart);
                  if (swap) {
                    showBundleSavingsNotification(match.config);
                    document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
                    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

                    return mockAddResponse(match.config, swap);
                  }
                } catch (e) {
                  console.error('[BundleWatcher] Interception swap failed:', e);
                } finally {
                  window.__isSwapping = false;
                }
              } else {
                const cartItems = (cart?.items || []).filter((item) => !isBuilderLine(item));
                for (const config of configs) {
                  const bundleVariantId = Number(config.bundle_product?.variantId || 0);
                  if (!newVariantIds.includes(bundleVariantId)) continue;

                  // Only components on the bundle's own purchase type are folded
                  // into it; a subscribed component stays put under a one-time
                  // bundle add, and vice versa.
                  const requiredQty = getRequiredComponentQty(config);
                  const componentIds = (config.component_products || []).map((p) => Number(p.variantId));
                  const componentsInCart = componentIds
                    .map((id) => findLineOfType(cartItems, id, newItemsHavePlan))
                    .filter(Boolean);

                  if (componentsInCart.length > 0) {
                    const updates = buildDecrementUpdates(
                      componentsInCart.map((line) => ({ line, qty: requiredQty }))
                    );
                    try {
                      const preClearRes = await fetchWithRetry('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        body: JSON.stringify({ updates }),
                      });

                      if (preClearRes && preClearRes.ok) {
                        invalidateSharedCartState();
                        console.log('[BundleWatcher] Pre-cleared components before direct bundle add:', Object.keys(updates));
                      } else {
                        console.warn('[BundleWatcher] Pre-clear failed -- orphan safety net (Path C-3) will handle on next evaluation');
                      }
                    } catch (e) {
                      console.error('[BundleWatcher] Pre-add component cleanup failed:', e);
                    }
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }

    return _originalFetch(input, init);
  };

  // Path C: Safety-net evaluation

  async function evaluateCart() {
    if (window.__isSwapping) return;

    const configs = getSortedConfigs();
    if (configs.length === 0) return;

    const cart = await fetchCart();
    if (!cart) return;

    const componentMatch = findMatchingBundle(cart, configs);
    if (componentMatch) {
      window.__isSwapping = true;
      try {
        const { config, componentLines, isSubscribed } = componentMatch;
        if (await performSwap(config, componentLines, isSubscribed, {}, cart)) {
          showBundleSavingsNotification(config);
          document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        }
      } finally {
        window.__isSwapping = false;
      }
      return;
    }

    const upgradeMatch = findBundleUpgrade(cart, configs);
    if (upgradeMatch) {
      window.__isSwapping = true;
      try {
        const { config, linesToRemove, isSubscribed } = upgradeMatch;
        if (await performSwap(config, linesToRemove, isSubscribed, {}, cart)) {
          showBundleSavingsNotification(config);
          document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        }
      } finally {
        window.__isSwapping = false;
      }
      return;
    }

    // Intentionally disabled: orphan auto-cleanup could remove user-intended
    // standalone quantities for bundle component products.
  }

  function scheduleEvaluation() {
    if (window.__isSwapping) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluateCart, DEBOUNCE_MS);
  }

  if ((window.BUNDLE_CONFIGS || []).length > 0) {
    document.addEventListener('cart:updated', scheduleEvaluation);
    document.addEventListener('cart:opened', scheduleEvaluation);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleEvaluation);
    } else {
      scheduleEvaluation();
    }
  }

  window.__bundleWatcherEvaluate = evaluateCart;

  console.log('[BundleWatcher] Initialized with', (window.BUNDLE_CONFIGS || []).length, 'config(s)');
})();
